import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from './ConfirmDialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Funnel, Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { RuleBuilder, ruleChipSummary, ruleFacetCount, projectStatusOptions } from './CustomFilterBuilder';
import { countMatches } from '../lib/projectFilterRules';

// Project Settings → Custom Filters.
// Dedicated management screen (moved off the board) for creating, editing and
// deleting the project's saved filter library. Filters are shared across the
// whole project team.
export default function ProjectFiltersManager({ projectId, project, members = [], timezone = 'UTC', tasks = [] }) {
  const { getOrgHeaders, API } = useAuth();
  const confirm = useConfirm();
  const [filters, setFilters] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { filter_id?, name, rules }

  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const statuses = projectStatusOptions(project);
  const customFields = project?.custom_fields || [];
  const allTags = React.useMemo(() => {
    const set = new Set();
    (tasks || []).forEach(t => (t.tags || []).forEach(tag => set.add(tag)));
    return Array.from(set).sort();
  }, [tasks]);

  const fetchFilters = useCallback(async () => {
    if (!projectId) return;
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/filters`, h());
      setFilters(data);
    } catch { /* keep empty */ }
  }, [API, projectId, h]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  const openCreate = () => { setEditing({ name: '', rules: {} }); setDialogOpen(true); };
  const openEdit = (f) => { setEditing({ ...f, rules: { ...(f.rules || {}) } }); setDialogOpen(true); };

  const saveFilter = async () => {
    if (!editing?.name?.trim()) { toast.error('Filter name is required'); return; }
    try {
      if (editing.filter_id) {
        const { data } = await axios.put(`${API}/projects/${projectId}/filters/${editing.filter_id}`, { name: editing.name, rules: editing.rules }, h());
        setFilters(prev => prev.map(f => f.filter_id === data.filter_id ? data : f));
        toast.success('Filter updated');
      } else {
        const { data } = await axios.post(`${API}/projects/${projectId}/filters`, { name: editing.name, rules: editing.rules }, h());
        setFilters(prev => [...prev, data]);
        toast.success('Filter created');
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save filter'); }
  };

  const removeFilter = async (f) => {
    if (!(await confirm({ title: `Delete "${f.name}"?`, description: 'This custom filter will be permanently removed for everyone on the project.', confirmText: 'Delete', variant: 'destructive' }))) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/filters/${f.filter_id}`, h());
      setFilters(prev => prev.filter(x => x.filter_id !== f.filter_id));
    } catch { toast.error('Delete failed'); }
  };

  const facet = editing?.rules ? ruleFacetCount(editing.rules) : 0;

  return (
    <section id="custom-filters" data-testid="custom-filters-section" className="scroll-mt-24">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-[15px] font-bold text-zinc-800 flex items-center gap-2">
            <Funnel size={16} weight="duotone" className="text-blue-600" /> Custom Filters
          </h2>
          <p className="text-[12px] text-zinc-500 mt-0.5">Reusable saved filters for this project's board and list. Available to everyone on the project.</p>
        </div>
        <Button size="sm" className="h-8 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={openCreate} data-testid="create-custom-filter-btn">
          <Plus size={14} weight="bold" className="mr-1" /> New filter
        </Button>
      </div>

      <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden bg-white">
        {filters.length === 0 && (
          <p className="text-center text-[13px] text-zinc-400 py-10">No custom filters yet. Create one to slice your board quickly.</p>
        )}
        {filters.map(f => (
          <div key={f.filter_id} className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50/60 group" data-testid={`custom-filter-row-${f.filter_id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-semibold text-zinc-800 truncate">{f.name}</p>
              <p className="text-[11px] text-zinc-400 truncate">{ruleChipSummary(f.rules)}</p>
            </div>
            <button onClick={() => openEdit(f)} className="p-1.5 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Edit" data-testid={`edit-custom-filter-${f.filter_id}`}>
              <PencilSimple size={15} />
            </button>
            <button onClick={() => removeFilter(f)} className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete" data-testid={`delete-custom-filter-${f.filter_id}`}>
              <Trash size={15} />
            </button>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden p-0 gap-0" data-testid="custom-filter-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-zinc-100">
            <DialogTitle className="text-[18px] font-bold text-zinc-900 flex items-center gap-2">
              <Funnel size={18} weight="duotone" className="text-blue-600" />
              {editing?.filter_id ? 'Edit custom filter' : 'New custom filter'}
              {facet > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">{facet} active</span>}
              {facet > 0 && tasks?.length > 0 && (
                <span className="ml-auto px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold flex items-center gap-1.5" data-testid="custom-filter-preview-count">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  Matches {countMatches(tasks, editing.rules, { timezone })} of {tasks.length}
                </span>
              )}
            </DialogTitle>
            <p className="text-[12px] text-zinc-500 mt-0.5">Combine any number of rules — tasks must match all selected criteria.</p>
            <DialogDescription className="sr-only">Build a reusable custom filter by combining status, priority, type, assignee and date rules.</DialogDescription>
          </DialogHeader>
          <div className="px-6 py-4 max-h-[calc(85vh-180px)] overflow-y-auto">
            <div className="mb-5">
              <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-[0.1em]">Filter name</Label>
              <Input
                autoFocus
                value={editing?.name || ''}
                onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. My open bugs · Stale tickets"
                className="mt-1.5 h-10 text-sm"
                data-testid="custom-filter-name"
              />
            </div>
            {editing && (
              <RuleBuilder
                rules={editing.rules || {}}
                statuses={statuses}
                members={members}
                customFields={customFields}
                allTags={allTags}
                onChange={(rules) => setEditing(prev => ({ ...prev, rules }))}
              />
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-zinc-100 bg-zinc-50/40">
            {facet > 0 && (
              <button type="button" onClick={() => setEditing(prev => ({ ...prev, rules: {} }))} className="text-[12px] font-medium text-zinc-500 hover:text-zinc-800 mr-auto px-2" data-testid="custom-filter-reset">
                Reset rules
              </button>
            )}
            <Button variant="outline" className="h-9 rounded-lg" onClick={() => { setDialogOpen(false); setEditing(null); }}>Cancel</Button>
            <Button className="h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={saveFilter} data-testid="custom-filter-save">
              {editing?.filter_id ? 'Update filter' : 'Create filter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
